<template>
  <div class="c-overview">
    <md-toolbar md-elevation="0" class="md-transparent">
      <div class="md-toolbar-row actions">
        <div class="md-toolbar-section-start" v-if="displayFilter">
          <md-switch class="" v-model="filterFlagged">bewaarde momenten</md-switch>
        </div>
        <div class="md-toolbar-section-end">
          <md-button class="" @click="switchToCurrent"
            ><span>{{ currentPeriodName }}</span></md-button
          >
          <md-menu md-direction="bottom-start">
            <md-button class="md-button-with-icon md-raised" md-menu-trigger
              ><md-icon>filter_list</md-icon><span>{{ periodNameById }}</span></md-button
            >
            <md-menu-content>
              <md-menu-item @click="switchPeriod(periods.PERIOD_DAY)">{{ translate(thingsToTranslate.day) }}</md-menu-item>
              <md-menu-item @click="switchPeriod(periods.PERIOD_WEEK)">{{ translate(thingsToTranslate.week) }}</md-menu-item>
              <md-menu-item @click="switchPeriod(periods.PERIOD_MONTH)">{{ translate(thingsToTranslate.month) }}</md-menu-item>
            </md-menu-content>
          </md-menu>
        </div>
      </div>
      <div class="md-toolbar-row">
        <md-button class="md-icon-button" @click="prevDayHandle">
          <md-icon>keyboard_arrow_left</md-icon>
        </md-button>
        <h3>{{ displayDate }}</h3>
        <md-button class="md-icon-button" @click="nextDayHandle">
          <md-icon>keyboard_arrow_right</md-icon>
        </md-button>
      </div>
    </md-toolbar>
    <div v-if="isLoading">
      <md-progress-spinner :md-diameter="30" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
    </div>

    <md-empty-state v-if="!isLoading && this.moments.length === 0" md-rounded md-icon="room_service" :md-size="288">
      <p>{{ translate(thingsToTranslate.emptystate) }} ...</p>
      <md-button class="md-primary md-raised md-dense" to="add-moment">voeg toe</md-button>
    </md-empty-state>

    <div class="md-layout md-gutter">
      <div class="md-layout-item md-size-33 md-small-size-50 md-xsmall-size-100" v-for="(moment, index) in filteredMoments" :key="index">
        <md-card :class="{ 'warning-signs': moment.warningSigns }">
          <md-card-header>
            <md-card-header-text>
              <md-button class="md-icon-button" @click="editMomentHandle(moment.id)"><md-icon>edit</md-icon></md-button>
              <div class="md-title" v-if="moment.type">
                {{ translate(momentType(moment.type).name) }}
              </div>
              <div class="md-subhead">
                <md-icon :class="{ skipped: moment.skipped }" class="">access_time</md-icon>
                {{ moment.dateTime | relativeDate }}
                <span v-if="moment.location"><md-icon>place</md-icon>{{ translate(location(moment.location).name) }}</span>
                <span v-if="moment.party"><md-icon>people</md-icon>{{ translate(party(moment.party).name) }}</span>
              </div>
            </md-card-header-text>
          </md-card-header>
          <md-card-media
            :class="{ has_image: moment.media }"
            :style="{ backgroundImage: moment.type ? 'url(' + require('./../../assets/svg/' + momentType(moment.type).iconId + '.svg') + ')' : none }"
          >
            <img v-if="moment.media" :src="moment.media | resizeImage({ height: 125, width: 512, crop: 'fill' })" />
            <div class="c-card-media-caption">{{ moment.summary }}</div>
          </md-card-media>
          <md-card-expand>
            <md-card-actions md-alignment="space-between">
              <div>
                <md-button class="md-icon-button" @click="saveMomentHandle(moment)">
                  <md-icon class="flagged" v-if="moment.flagged">bookmark</md-icon>
                  <md-icon v-else>bookmark_border</md-icon>
                </md-button>
              </div>
              <md-card-expand-trigger>
                <md-button class="md-icon-button">
                  <md-icon>keyboard_arrow_down</md-icon>
                </md-button>
              </md-card-expand-trigger>
            </md-card-actions>

            <md-card-expand-content>
              <md-card-content>
                {{ moment.description }}
                <p class="u-txt--modest">
                  <span v-for="(emotion, indx) in moment.emotionObjects" :key="indx" class="u-cs-list">
                    {{ translate(emotion.name) }}
                  </span>
                </p>
              </md-card-content>

              <div class="c-card-list" v-if="moment.behaviourObjects.length">
                <span v-for="(behaviour, indx) in moment.behaviourObjects" :key="indx" class="u-cs-list">
                  {{ translate(behaviour.name) }}
                </span>
              </div>
            </md-card-expand-content>
          </md-card-expand>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import momentService from "../../services/momentservice";
import * as repos from "../../contentrepositories";
import { navigate, translate, momentVariations } from "../../mixins";
import { isSameWeek, isThisWeek, addWeeks, startOfWeek, endOfWeek, isThisMonth, isSameMonth, addMonths, addDays, startOfMonth, endOfMonth } from "date-fns";

export default {
  name: "OverVue",
  mixins: [navigate, translate, momentVariations],
  created() {
    this.ensureMoments();
  },
  computed: {
    ...mapState(["selectedDate", "user", "selectedPeriod"]),
    displayDate() {
      if (this.isPeriodSelected(this.periods.PERIOD_WEEK)) {
        if (isThisWeek(this.selectedDate)) {
          return this.translate(this.thingsToTranslate.thisWeek);
        } else if (isSameWeek(this.selectedDate, addWeeks(new Date(), 1))) {
          return this.translate(this.thingsToTranslate.nextWeek);
        } else if (isSameWeek(this.selectedDate, addWeeks(new Date(), -1))) {
          return this.translate(this.thingsToTranslate.previousWeek);
        }

        return `${this.$relativeDate(startOfWeek(this.selectedDate), "PPP")} - ${this.$relativeDate(endOfWeek(this.selectedDate), "PPP")}`;
      } else if (this.isPeriodSelected(this.periods.PERIOD_MONTH)) {
        if (isThisMonth(this.selectedDate)) {
          return this.translate(this.thingsToTranslate.thisMonth);
        } else if (isSameMonth(this.selectedDate, addMonths(new Date(), 1))) {
          return this.translate(this.thingsToTranslate.nextMonth);
        } else if (isSameMonth(this.selectedDate, addMonths(new Date(), -1))) {
          return this.translate(this.thingsToTranslate.previousMonth);
        }

        return this.$relativeDate(this.selectedDate, "MMMM yyyy");
      }

      return this.$date(this.selectedDate, "PPPP", undefined, 7, true);

      // return this.$moment(this.selectedDate).calendar(null, {
      //   sameDay: `[${this.translate(this.thingsToTranslate.today)}]`,
      //   nextDay: `[${this.translate(this.thingsToTranslate.tomorrow)}]`,
      //   nextWeek: "ll",
      //   lastDay: `[${this.translate(this.thingsToTranslate.yesterday)}]`,
      //   lastWeek: "ll",
      //   sameElse: "ll"
      // });
    },
    displayFilter() {
      return !this.isLoading && this.moments.length > 1 && this.flaggedMoments(this.moments).length > 1;
    },
    filteredMoments() {
      if (this.filterFlagged) {
        return this.flaggedMoments(this.moments);
      }
      return this.moments;
    },
    currentPeriodName() {
      if (this.selectedPeriod === 0) {
        return this.translate(this.thingsToTranslate.today);
      } else if (this.selectedPeriod === 1) {
        return this.translate(this.thingsToTranslate.thisWeek);
      }
      return this.translate(this.thingsToTranslate.thisMonth);
    },
    periodNameById() {
      if (this.selectedPeriod === 0) {
        return this.translate(this.thingsToTranslate.day);
      } else if (this.selectedPeriod === 1) {
        return this.translate(this.thingsToTranslate.week);
      }
      return this.translate(this.thingsToTranslate.month);
    }
  },
  data() {
    return {
      msg: "OverVue",
      moments: [],
      isLoading: true,
      filterFlagged: false,
      periods: {
        PERIOD_DAY: 0,
        PERIOD_WEEK: 1,
        PERIOD_MONTH: 2
      },
      thingsToTranslate: {
        emptystate: {
          en: "Start your first entry here",
          nl: "Begin hier met je eerste log"
        },
        day: {
          en: "day",
          nl: "dag"
        },
        today: {
          en: "Today",
          nl: "Vandaag"
        },
        tomorrow: {
          en: "Tomorrow",
          nl: "Morgen"
        },
        yesterday: {
          en: "Yesterday",
          nl: "Gisteren"
        },
        week: {
          en: "week",
          nl: "week"
        },
        thisWeek: {
          en: "This week",
          nl: "Deze week"
        },
        nextWeek: {
          en: "Next week",
          nl: "Volgende week"
        },
        previousWeek: {
          en: "Previous week",
          nl: "Vorige week"
        },
        month: {
          en: "month",
          nl: "maand"
        },
        thisMonth: {
          en: "This month",
          nl: "Deze maand"
        },
        nextMonth: {
          en: "Next month",
          nl: "Volgende maand"
        },
        previousMonth: {
          en: "Previous month",
          nl: "Vorige maand"
        }
      }
    };
  },
  methods: {
    momentType(id) {
      return id ? repos.momentTypeRepository.getById(id) : {};
    },
    location(id) {
      return id ? repos.locationRepository.getById(id) : {};
    },
    party(id) {
      return id ? repos.partyRepository.getById(id) : {};
    },
    saveMomentHandle(moment) {
      moment.flagged = !moment.flagged;
      momentService.saveMoment(moment);
    },
    increaseDate(num) {
      if (this.isPeriodSelected(this.periods.PERIOD_WEEK)) {
        this.$store.dispatch("setSelectedDate", addWeeks(this.selectedDate, num));
      } else if (this.isPeriodSelected(this.periods.PERIOD_MONTH)) {
        this.$store.dispatch("setSelectedDate", addMonths(this.selectedDate, num));
      } else {
        this.$store.dispatch("setSelectedDate", addDays(this.selectedDate, num));
      }
    },
    nextDayHandle() {
      this.increaseDate(1);
    },
    prevDayHandle() {
      this.increaseDate(-1);
    },
    editMomentHandle(id) {
      this.$router.push({ name: "EditMoment", params: { momentId: id } });
    },
    switchPeriod(period) {
      this.$store.dispatch("setSelectedPeriod", period);
    },
    switchToCurrent() {
      this.$store.dispatch("setSelectedDate", new Date());
    },
    isPeriodSelected(period) {
      return this.selectedPeriod === period;
    },
    async ensureMoments() {
      this.isLoading = true;
      this.moments = [];
      const currentLoadingToken = {};
      this.loadingMomentsPromise = currentLoadingToken;
      let date = this.selectedDate;
      if (this.isPeriodSelected(this.periods.PERIOD_WEEK)) {
        date = {
          start: startOfWeek(this.selectedDate),
          end: endOfWeek(this.selectedDate)
        };
      } else if (this.isPeriodSelected(this.periods.PERIOD_MONTH)) {
        date = {
          start: startOfMonth(this.selectedDate),
          end: endOfMonth(this.selectedDate)
        };
      }
      const result = await momentService.getMoments(this.user.userId, date);
      if (currentLoadingToken === this.loadingMomentsPromise) {
        this.isLoading = false;
        this.moments = result.map(moment => this.expandedMoment(moment));
      }
    }
  },
  watch: {
    selectedPeriod() {
      this.ensureMoments();
    },
    selectedDate() {
      this.ensureMoments();
    },
    user() {
      this.ensureMoments();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/libs.vuematerial-cards.scss";

.c-overview {
  max-width: 1140px;
  margin: auto;
  margin-bottom: 100px;
  text-align: center;
  --md-theme-default-primary-on-background: var(--color-text);
  --md-theme-default-accent-on-: var(--color-warning);
  --md-theme-default-accent: var(--color-warning-2);
}

.md-toolbar-row {
  max-width: 325px;
  margin: 0 auto;
  justify-content: center;

  h3 {
    width: 210px;
  }

  &.actions {
    max-width: 100%;

    ::v-deep .md-button {
      margin: 0 4px !important;
    }
  }
}

.md-list {
  background-color: #ffffff;
  border: 1px solid var(--color-border);
}

.md-subhead {
  span {
    margin-left: 0.2rem;
  }
}
.md-subhead {
  .md-icon {
    opacity: 0.35;
  }
}

::v-deep {
  .md-button-with-icon {
    .md-icon {
      margin: 0 8px 0 0;
    }
  }
  .md-switch-label {
    text-transform: uppercase;
    opacity: 0.5;

    .md-checked & {
      opacity: 1;
    }
  }
}
</style>
